class SearchDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get shouldAppendToBody() {
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class SearchTyped extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get startDelay() {
    return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
  }

  async init() {
    this.insertCursor();
    await this.start(this.getAttribute('data-first-text'), this.startDelay);

    setTimeout(async () => {
      await this.reset();
      await this.start(this.getAttribute('data-last-text'), 0);
    }, 600);
  }

  async start(text, delay) {
    this.innerHTML = text;
    await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
    this.cursor.classList.add('blink');
  }

  async reset() {
    this.cursor.classList.remove('blink');
    await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
  }

  insertCursor() {
    if (this.cursor) return;

    this.cursor = document.createElement('span');
    this.cursor.className = 'typed-cursor';
    this.cursor.setAttribute('aria-hidden', true);
    this.cursor.innerHTML = '|';
    this.parentElement.insertBefore(this.cursor, this.nextSibling);
  }
}
customElements.define('search-typed', SearchTyped);

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.historyKey = `${theme.settings.themeName}:recent-searches`;

    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('click', this.hideTypewriter.bind(this));
    this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('click', this.onSearchInteraction.bind(this));
    this.renderRecentSearches();
    this.syncDiscovery();
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get typewriter() {
    return this.input.previousElementSibling;
  }

  hideTypewriter() {
    if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
      this.typewriter.setAttribute('hidden', '');
    }
  }

  onFocus(event) {
    this.renderRecentSearches();
    this.syncDiscovery();

    if (this.closest('.collection')) {
      document.body.classList.add('predictive-search-open');

      const searchTerm = this.getQuery();
      if (searchTerm.length === 0) return;

      const url = this.buildUrl().toString();
      this.renderSection(url, event);
    }
  }

  clear(event = null) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.syncDiscovery();
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchQuery() {
    const query = this.getQuery();
    const normalized = query.toLocaleLowerCase().replace(/\s+/g, ' ').trim();
    const exactSynonyms = new Map([
      ['mock up', 'mockup'],
      ['drum kit', 'drumkit'],
      ['typo', 'font'],
      ['police', 'font'],
      ['preset', 'preset OR LUT'],
      ['lut', 'preset OR LUT']
    ]);

    return exactSynonyms.get(normalized) || query
      .replace(/\bmock\s+up\b/gi, 'mockup')
      .replace(/\bdrum\s+kit\b/gi, 'drumkit')
      .replace(/\btypo\b/gi, 'font')
      .replace(/\bpolice\b/gi, 'font');
  }

  buildUrl() {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

    url.searchParams.set('q', this.getSearchQuery());
    url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'query,product,collection,article,page');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    url.searchParams.set('section_id', theme.utils.sectionId(this));
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.hideTypewriter();
    this.syncDiscovery();

    const url = this.buildUrl().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);

    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    
    this.setAttribute('loading', '');

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);

        this.setAttribute('results', '');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      })
      .finally(() => this.removeAttribute('loading'));
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
    if (document.getElementById(id) === null) return;

    const responseResults = new DOMParser()
      .parseFromString(responseText, 'text/html')
      .getElementById(id);
    if (!responseResults) return;

    document.getElementById(id).innerHTML = responseResults.innerHTML;
  }

  syncDiscovery() {
    const discovery = this.querySelector('[data-search-discovery-box]');
    if (!discovery) return;

    discovery.hidden = this.getQuery().length > 0;
  }

  onSubmit() {
    const originalQuery = this.getQuery();
    if (!originalQuery) return;

    this.saveRecentSearch(originalQuery);
    this.input.value = this.getSearchQuery();
    HytrapeSearchInsights.track('search_submit', { query: originalQuery, normalizedQuery: this.input.value });
  }

  onSearchInteraction(event) {
    const clearButton = event.target.closest('[data-clear-search-history]');
    if (clearButton) {
      event.preventDefault();
      try {
        window.localStorage.removeItem(this.historyKey);
      }
      catch {
        return;
      }
      this.renderRecentSearches();
      return;
    }

    const queryLink = event.target.closest('[data-search-query]');
    if (queryLink) this.saveRecentSearch(queryLink.dataset.searchQuery || queryLink.textContent.trim());

    const resultLink = event.target.closest('.search__results a');
    if (resultLink) {
      this.saveRecentSearch(this.getQuery());
      HytrapeSearchInsights.track('predictive_search_click', { query: this.getQuery(), destination: resultLink.href });
    }
  }

  saveRecentSearch(query) {
    const cleanQuery = query.trim();
    if (!cleanQuery) return;

    const current = this.getRecentSearches().filter((item) => item.toLocaleLowerCase() !== cleanQuery.toLocaleLowerCase());
    try {
      window.localStorage.setItem(this.historyKey, JSON.stringify([cleanQuery, ...current].slice(0, 5)));
    }
    catch {
      return;
    }
  }

  getRecentSearches() {
    try {
      const searches = JSON.parse(window.localStorage.getItem(this.historyKey) || '[]');
      return Array.isArray(searches) ? searches : [];
    }
    catch {
      return [];
    }
  }

  renderRecentSearches() {
    const history = this.querySelector('[data-search-history]');
    const list = this.querySelector('[data-search-history-list]');
    if (!history || !list) return;

    const searches = this.getRecentSearches();
    history.hidden = searches.length === 0;
    list.replaceChildren(...searches.map((query) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
      link.dataset.searchQuery = query;
      link.textContent = query;
      item.append(link);
      return item;
    }));
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

document.addEventListener('click', (event) => {
  const queryLink = event.target.closest('[data-search-query]');
  if (!queryLink) return;

  document.querySelector('form[is="predictive-search"]')?.saveRecentSearch(
    queryLink.dataset.searchQuery || queryLink.textContent.trim()
  );
});

class HytrapeSearchInsights {
  static storageKey = `${theme.settings.themeName}:search-insights`;

  static allowed() {
    const privacy = window.Shopify?.customerPrivacy;
    return !privacy?.analyticsProcessingAllowed || privacy.analyticsProcessingAllowed();
  }

  static track(eventName, detail = {}) {
    if (!this.allowed()) return;

    const payload = { event: `hytrape_${eventName}`, ...detail, timestamp: Date.now() };
    document.dispatchEvent(new CustomEvent('hytrape:search', { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    if (typeof window.gtag === 'function') window.gtag('event', eventName, detail);

    try {
      const events = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
      const history = Array.isArray(events) ? events : [];
      window.localStorage.setItem(this.storageKey, JSON.stringify([...history, payload].slice(-100)));
    }
    catch {
      return;
    }
  }

  static initPage() {
    const root = document.querySelector('[data-search-insights]');
    if (!root) return;

    const query = root.dataset.searchTerm || '';
    const resultCount = Number.parseInt(root.dataset.searchCount || '0', 10);
    if (!query) return;
    let clicked = false;

    this.track(resultCount === 0 ? 'zero_results' : 'results_view', { query, resultCount });
    root.addEventListener('click', (event) => {
      const resultLink = event.target.closest('#ProductGridContainer a');
      if (!resultLink) return;
      clicked = true;
      this.track('result_click', { query, resultCount, destination: resultLink.href });
    });
    window.addEventListener('pagehide', () => {
      if (!clicked && query) this.track('search_without_click', { query, resultCount });
    }, { once: true });
  }
}

HytrapeSearchInsights.initPage();

class PredictiveSearchOverlay extends OverlayElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  onClick() {
    setTimeout(() => {
      document.body.classList.remove('predictive-search-open');
    });
  }
}
customElements.define('predictive-search-overlay', PredictiveSearchOverlay);
